import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import FaIcon from "~/components/FaIcon";
import Subnav, { SubItem, SubLink } from "~/components/Subnav";
import ErrorBoundary from "~/components/ErrorBoundary";

import transition from "~/utilities/transition";
import trackNavigationEvent from "~/utilities/trackNavigationEvent";
import { sanitizeForId } from "~/utilities/helpers";

const SubNavButton = styled.button`
    ${transition({}, "text-decoration", "background-color")};
    width: 100%;
    text-align: left;
    padding: 10px 30px;
    font-size: 1.8rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.black};
    text-decoration: none;
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletLandscape}) {
        ${({ isNavOpened, theme }) =>
            isNavOpened
                ? `
            background-color: ${theme.palette.grayFive};
        `
                : ``};

        &:hover,
        &:focus {
            background-color: ${({ theme }) => theme.palette.grayFive};
        }
    }
`;

const ArrowRight = styled(FaIcon)`
    @media (forced-colors: active) {
        color: ButtonText;
    }
`;

const SubNavItem = ({
    subItems = [],
    linkLabel,
    linkUrl = "",
    newTabOverride = "default",
    index = -1,
    topLinkLabel,
    openedSubnavs = {},
    dispatch = () => {},
    level = "secondary",
    firstLinkLabel = "",
    lastItem = false,
    tertiarySubRefProp = {},
    tagRef = null,
}) => {
    const tertiaryLevel = level === "tertiary";
    const hasDropdown = subItems && !!subItems.length;
    const submenuId = `${sanitizeForId(topLinkLabel)}-${sanitizeForId(
        linkLabel,
    )}-${index}`;
    const isNavOpened = openedSubnavs.tertiary === submenuId;
    const linkParentLabel = tertiaryLevel
        ? sanitizeForId(firstLinkLabel)
        : sanitizeForId(topLinkLabel);

    const linkProps = {
        newTabOverride,
        to: linkUrl,
        onClick: () =>
            trackNavigationEvent(
                false,
                tertiaryLevel ? 3 : 2,
                `${
                    tertiaryLevel && firstLinkLabel
                        ? `${firstLinkLabel} > `
                        : ""
                }${topLinkLabel} > ${linkLabel}`,
            ),
        topLinkLabel: linkParentLabel,
        lastItem,
        tagRef,
    };

    const subNavButtonProps = {
        isNavOpened,
        onClick: () => {
            dispatch({
                type: "HANDLE_SUB_NAV",
                id: submenuId,
                currentSubNavs: openedSubnavs,
                level: "tertiary",
            });
            trackNavigationEvent(true, 2, `${topLinkLabel} > ${linkLabel}`);
        },
        type: "button",
        "aria-expanded": isNavOpened,
        "data-label": `${sanitizeForId(topLinkLabel)}-${sanitizeForId(
            linkLabel,
        )}`,
        "data-parent": linkParentLabel,
        "data-last": lastItem,
    };

    return (
        <SubItem>
            {hasDropdown > 0 ? (
                <ErrorBoundary>
                    <SubNavButton {...subNavButtonProps}>
                        {linkLabel}
                        <ArrowRight name="arrow-right" />
                    </SubNavButton>
                    <Subnav
                        level="tertiary"
                        isOpen={isNavOpened}
                        items={subItems}
                        topLinkLabel={linkLabel}
                        topLinkUrl={linkUrl}
                        topLinkNewTabOverride={newTabOverride}
                        firstLinkLabel={topLinkLabel}
                        {...{
                            openedSubnavs,
                            index,
                            tertiarySubRefProp,
                        }}
                    />
                </ErrorBoundary>
            ) : (
                <SubLink {...linkProps}>{linkLabel}</SubLink>
            )}
        </SubItem>
    );
};

SubNavItem.propTypes = {
    subItems: PropTypes.arrayOf(PropTypes.shape({})),
    linkLabel: PropTypes.string.isRequired,
    linkUrl: PropTypes.string,
    newTabOverride: PropTypes.string,
    index: PropTypes.number,
    topLinkLabel: PropTypes.string.isRequired,
    openedSubnavs: PropTypes.shape({}),
    dispatch: PropTypes.func,
    level: PropTypes.string,
    firstLinkLabel: PropTypes.string,
    lastItem: PropTypes.bool,
    tertiarySubRefProp: PropTypes.object,
    tagRef: PropTypes.object,
};

export default SubNavItem;
